import axios from 'axios';
import { request, authRequest } from '../utils/request';

/**
 * 根据 ClientId 获取系统访问地址（无需授权，不走业务 axios 拦截器）
 * GET /api/Auth/GetSystemUrlByClientId?clientId=xxx
 * @param {string} clientId
 * @returns {Promise<string>} 系统根地址，失败返回空字符串
 */
export const getSystemUrlByClientId = async (clientId) => {
  if (!clientId) return '';
  try {
    const baseURL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');
    const { data } = await axios.get(`${baseURL}/Auth/GetSystemUrlByClientId`, {
      params: { clientId },
      timeout: 10000,
    });
    if (typeof data === 'string') return data.trim();
    if (data?.res_status_code === '0') {
      const c = data.res_content;
      if (typeof c === 'string') return c.trim();
      if (c && typeof c === 'object') {
        return String(
          c.systemUri ?? c.SystemUri ?? c.systemUrl ?? c.url ?? ''
        ).trim();
      }
    }
    return String(
      data?.systemUri ??
        data?.SystemUri ??
        data?.systemUrl ??
        data?.res_content ??
        data?.data ??
        ''
    ).trim();
  } catch {
    return '';
  }
};

/**
 * 登录接口
 * @param {Object} data - 登录数据
 * @param {string} data.Username - 用户名
 * @param {string} data.Password - 密码（MD5加密）
 * @param {boolean} data.RememberLogin - 记住登录
 * @param {string} data.ReturnUrl - 返回URL
 * @param {string} data.RequestId - 请求ID（用于人机校验）
 * @param {Array} data.RequestData - 请求数据
 * @param {string} data.ClientId - 客户端ID
 * @param {string} data.PlantCode - 三方登录平台代码（可选）
 * @param {string} data.RedirectUri - 三方登录重定向URI（可选）
 * @param {string} data.Code - 三方登录Code（可选）
 */
export const login = (data) => {
  // 使用 authRequest，baseURL 为 VITE_APP_OIDC_AUTHORITY
  return authRequest({
    url: 'account/Login',
    method: 'post',
    data,
    withCredentials: true,
    headers: {
      // @ts-ignore
      ignoreCancelToken: true,
    },
  });
};

/**
 * 登出接口
 * @param {string} params.logoutId - 登出ID
 * @returns {Promise} 返回登出结果
 */
export const logout = (params) => {
  // 使用 authRequest，baseURL 为 VITE_APP_OIDC_AUTHORITY
  return authRequest({
    url: 'account/logout',
    method: 'get',
    params,
    withCredentials: true,
    headers: {
      // @ts-ignore
      ignoreCancelToken: true,
    },
  });
};

/**
 * 获取用户信息
 */
export const getUserInfo = () => {
  return request({
    url: '/userInfo',
    method: 'get',
    isError: true,
    data: qs.stringify(data),
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    baseURL: process.env.BASE_OIDC_Server_URL
  });
};

/**
 * 刷新token
 */
export const refreshToken = () => {
  return request({
    url: 'connect/token',
    method: 'post',
  });
};

/**
 * 获取飞书登录二维码
 * @returns {Promise} 返回二维码信息 { qrCodeUrl, ticket, expiresIn }
 */
export const getFeishuQRCode = () => {
  return request({
    url: '/feishu/qrcode',
    method: 'get',
  });
};

/**
 * 检查飞书二维码登录状态
 * @param {string} ticket - 二维码ticket
 * @returns {Promise} 返回登录状态 { status, token, userInfo }
 */
export const checkFeishuLoginStatus = (ticket) => {
  return request({
    url: `/feishu/status/${ticket}`,
    method: 'get',
  });
};